/**
 * The one map style, and the one way to load MapLibre.
 *
 * MapLibre and its worker are half a megabyte. The map screen is allowed to pay that, because it
 * is a map; a photo sheet with a folded-away mini map is not, and importing it statically from
 * anywhere in the media tab would put it in the bundle of everyone who never opens either. So
 * the library is fetched the first time a map is actually drawn, and never before.
 */

import type { StyleSpecification } from "maplibre-gl";

/** OpenFreeMap (owner choice, 2026-10-11): OpenStreetMap vector tiles, no key, light style. */
export const MAP_STYLE = "https://tiles.openfreemap.org/styles/positron";

/**
 * What the map draws on when the real style cannot be had: a flat, visibly neutral background and
 * nothing else. It has no sources to wait for, so MapLibre fires "load" at once and the layers
 * the screen adds (position, danger zones, recordings) draw on top. The basemap itself is not
 * available offline and is not meant to be: OSM tiles may not be prefetched (feasibility F27).
 * The glyph URL is only there so a layer with text does not error. Offline the range request
 * fails and MapLibre draws the glyphs locally in a fallback font, so cluster counts still show.
 *
 * Not covered: a tile or style request that hangs after the real style was served (lie-fi). A
 * request that fails still lets "load" fire; one that never answers does not, and the map stays
 * an empty component. That case is deliberately left unhandled (no retries).
 */
export const BLANK_STYLE: StyleSpecification = {
  version: 8,
  sources: {},
  glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
  layers: [{ id: "background", type: "background", paint: { "background-color": "#dde3ea" } }],
};

/**
 * How long the style request is given before the blank style is used instead.
 *
 * It decides more than it looks like it does, because there is no retry: whichever style the map
 * is built with is the one it keeps for the whole session. Four seconds was a regression on a slow
 * but working mobile connection, which used to get real tiles eventually (the map was handed the
 * URL and waited as long as it took) and now got a blank basemap for good. Ten seconds covers a
 * small JSON document on any connection that is actually usable. The cost is on the connection
 * that is not, one that opens and then stalls: it shows nothing for ten seconds before the zones
 * and routes draw on the blank style, where before this branch it showed nothing for ever.
 *
 * A late `map.setStyle` once the real style does arrive was considered and not done: it replaces
 * the whole style, which drops every source and layer the screen has added since the map was
 * built, so a naive call would wipe the routes and the danger zones from under a traveller.
 */
const STYLE_TIMEOUT_MS = 10_000;

/**
 * The real style when OpenFreeMap answers, `BLANK_STYLE` when it does not.
 *
 * Fetched here rather than left to MapLibre: given a style URL that is unreachable it never fires "load", and everything the map screen draws waits for "load". Deciding first means the map
 * always gets a style it can finish loading. The real style is returned exactly as served, so its
 * source attribution (a licence obligation) is untouched.
 */
export async function loadStyle(): Promise<StyleSpecification> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), STYLE_TIMEOUT_MS);
  try {
    const res = await fetch(MAP_STYLE, { signal: abort.signal });
    if (!res.ok) return BLANK_STYLE;
    const style: unknown = await res.json();
    const s = style as Partial<StyleSpecification> | null;
    if (!s || s.version !== 8 || !Array.isArray(s.layers)) return BLANK_STYLE;
    return s as StyleSpecification;
  } catch {
    return BLANK_STYLE;
  } finally {
    clearTimeout(timer);
  }
}

let loading: Promise<typeof import("maplibre-gl")> | null = null;

/** MapLibre, its stylesheet and its worker, loaded once and shared by every caller. */
export function loadMapLibre(): Promise<typeof import("maplibre-gl")> {
  loading ??= (async () => {
    const [ml, worker] = await Promise.all([
      import("maplibre-gl"),
      // MapLibre 6 runs its worker from a separate file; Vite must bundle it self-contained.
      import("maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url"),
      import("maplibre-gl/dist/maplibre-gl.css"),
    ]);
    ml.setWorkerUrl(worker.default);
    return ml;
  })();
  return loading;
}
